import React from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';


import { IntegrationToken } from '../../../types/responses/Interoperability';
import StatusBadge from '../../atoms/StatusBadge';
import {
  createdLabel,
  isSensitiveScope,
  lastUseLabel,
  scopeLabel,
  tokenStatus,
} from '../../organisms/Interoperability/ApiTab/integrationLabels';


/* Mesmos blocos do detalhe de conexões, para as duas laterais do módulo se lerem igual. */

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h4`
  border-bottom: 1px solid #d1d5db;
  margin: 0 -16px;
  padding: 0 16px 8px;
`;

const Row = styled.p`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #8d929e;
  font-weight: 400;
  font-size: 12px;
  border-bottom: 1px solid #f3f4f6;
  padding: 8px 0;
  margin-bottom: 0;

  &:last-of-type {
    border-bottom: none;
  }
`;

const Value = styled.span`
  color: #000;
  font-size: 12px;
  text-align: right;
  margin-left: 8px;
`;

const Mono = styled(Value)`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const Hint = styled.p`
  color: #8a9099;
  font-size: 11px;
  line-height: 1.5;
  margin: 8px 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  background: #fff;
  color: ${({ danger }) => (danger ? '#b02525' : '#4a5462')};
  border: 1px solid ${({ danger }) => (danger ? '#f3c7c7' : '#d9dce1')};
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 12px;

  &:disabled {
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

const Sensitive = styled.span`
  color: #a8500f;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-left: 6px;
`;

export type IntegrationTokenDrawerDetailsProps = {
  token: IntegrationToken;
  busy: boolean;

  onRenew: () => void;
  onRevoke: () => void;
  onExtend: () => void;
  onRecreate: () => void;
  onDelete: () => void;
};

/**
 * Detalhe do token na lateral, no mesmo desenho do detalhe de conexões.
 *
 * As ações vivem aqui, e não na linha da tabela, por dois motivos. O primeiro é
 * consistência: o financeiro e a aba Conexões abrem detalhe na lateral, e a linha só leva o
 * chevron. O segundo é que estas ações precisam de uma frase para não serem confundidas —
 * renovar troca o segredo, recriar nasce outro token, estender mantém o mesmo. Botão solto
 * numa célula não tem onde dizer isso.
 */
export const IntegrationTokenDrawerDetails: React.FC<IntegrationTokenDrawerDetailsProps> = ({
  token,
  busy,
  onRenew,
  onRevoke,
  onExtend,
  onRecreate,
  onDelete,
}) => {
  const status = tokenStatus(token);

  return (
    <div>
      <Card>
        <CardTitle>{T('Token')}</CardTitle>

        <Row>
          {T('Integrator')}
          <Value>{token.name}</Value>
        </Row>
        <Row>
          {T('Token')}
          {token.token_hint ? (
            <Mono>move_key_…{token.token_hint}</Mono>
          ) : (
            <Value>{T('not identified')}</Value>
          )}
        </Row>
        <Row>
          {T('Status')}
          <StatusBadge tone={{ bg: status.bg, fg: status.fg, dot: status.dot }}>
            {status.label}
          </StatusBadge>
        </Row>
        <Row>
          {T('Representative e-mail')}
          <Value>{token.contact_email || '—'}</Value>
        </Row>

        {/* O valor completo não existe em lugar nenhum: o servidor guarda hash. Dizer isso
            aqui evita que alguém procure um botão de revelar que não vai existir. */}
        <Hint>
          {T('The full value is never shown again — only these last characters identify it.')}
        </Hint>
      </Card>

      <Card>
        <CardTitle>{T('Scopes')}</CardTitle>

        <Row>
          {T('Granted')}
          <Value>
            {token.scopes.map((scope) => (
              <Tag key={scope} color={isSensitiveScope(scope) ? 'orange' : undefined}>
                {scopeLabel(scope)}
                {isSensitiveScope(scope) && (
                  <Sensitive>
                    <WarningOutlined />
                  </Sensitive>
                )}
              </Tag>
            ))}
          </Value>
        </Row>
      </Card>

      <Card>
        <CardTitle>{T('History')}</CardTitle>

        <Row>
          {T('Created')}
          <Value>{createdLabel(token)}</Value>
        </Row>
        {token.created_by && (
          <Row>
            {T('Created by')}
            <Value>{token.created_by}</Value>
          </Row>
        )}
        {token.renewed_at && (
          <Row>
            {T('Last renewal')}
            <Value>{new Date(token.renewed_at).toLocaleString()}</Value>
          </Row>
        )}
        <Row>
          {T('Last use')}
          <Value>{lastUseLabel(token)}</Value>
        </Row>
        <Row>
          {T('Validity')}
          <Value>
            {token.expires_at
              ? new Date(token.expires_at).toLocaleString()
              : T('does not expire')}
          </Value>
        </Row>
        {token.revoked_at && (
          <Row>
            {T('Revoked on')}
            <Value>{new Date(token.revoked_at).toLocaleString()}</Value>
          </Row>
        )}
      </Card>

      <Card>
        <CardTitle>{T('Actions')}</CardTitle>

        {/*
          As ações dependem do estado, e a frase de cada bloco é o que impede o erro: quem vê
          "recriar" precisa saber que o segredo antigo não volta, e quem vê "estender"
          precisa saber que o segredo continua o mesmo.
        */}
        {token.revoked_at && (
          <>
            <Actions>
              <ActionButton type="button" disabled={busy} onClick={onRecreate}>
                {T('Recreate')}
              </ActionButton>
              <ActionButton type="button" danger disabled={busy} onClick={onDelete}>
                {T('Delete')}
              </ActionButton>
            </Actions>
            <Hint>
              {T(
                'Creates a new token with the same name and scopes. This one stays revoked, and its secret never comes back.',
              )}{' '}
              {T('Delete removes this revoked token for good — the audit trail keeps the record.')}
            </Hint>
          </>
        )}

        {!token.revoked_at && !token.active && (
          <>
            <Actions>
              <ActionButton type="button" disabled={busy} onClick={onExtend}>
                {T('Extend validity')}
              </ActionButton>
              <ActionButton type="button" danger disabled={busy} onClick={onRevoke}>
                {T('Revoke')}
              </ActionButton>
            </Actions>
            <Hint>
              {T(
                'Expired, not compromised: extending brings the same secret back, so the integrator does not have to change anything.',
              )}
            </Hint>
          </>
        )}

        {token.active && (
          <>
            <Actions>
              <ActionButton type="button" disabled={busy} onClick={onRenew}>
                {T('Renew')}
              </ActionButton>
              <ActionButton type="button" danger disabled={busy} onClick={onRevoke}>
                {T('Revoke')}
              </ActionButton>
            </Actions>
            <Hint>
              {T(
                'Renewing swaps the secret and keeps name and scopes — the integrator has to be given the new value. Revoking ends this access for good.',
              )}
            </Hint>
          </>
        )}
      </Card>
    </div>
  );
};
